import type { CSSProperties } from "react";

import type { Metadata } from "@/lib/head";
import { Link } from "@/lib/nav";

import Banner from "@buildpy/ui/banner";
import EmptyState from "@buildpy/ui/empty-state";
import FilterChips, { FilterChipItem } from "@/components/filter-chips";
import ListSearch from "@/components/list-search";
import Pager from "@/components/pager";
import Pill from "@buildpy/ui/pill";
import ResponsiveList, { ListCard } from "@/components/responsive-list";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import ListTable from "@buildpy/ui/list-table";
import {
  TableCell,
  TableRow,
} from "@buildpy/ui/table";
import ViewSwitch, { type ViewSwitchItem } from "@/components/view-switch";
import { EMPTY_MARK } from "@buildpy/core/lib/format";
import { cn } from "@/lib/utils";
import { listHref } from "@buildpy/core/modules/list/search";
import type { ProjectDue } from "@buildpy/core/modules/list/specs";
import { shiftMonth } from "@buildpy/core/modules/project/phase-chart";
import { countsPhaseDelay } from "@buildpy/core/modules/project/phase-schedule";
import { listStartDue, projectPrimaryActionOf } from "@buildpy/core/modules/project/primary-action";
import { needsRecordToday, recordGapOf, type ProjectTodaySignals } from "@buildpy/core/modules/project/todo-order";
import {
  buildSiteChart,
  chartMonthOf,
  currentPhaseTextOf,
  listPhaseProgressOf,
  type SiteChartPhase,
} from "@buildpy/core/modules/project/site-chart";
import { buildSiteCalendar, siteCalendarMetaOf } from "@buildpy/core/modules/project/site-calendar";
import { RECORDABLE_PROJECT_STATUSES } from "@buildpy/core/modules/project/transitions";
import { PROJECT_STATUS_LABEL, PROJECT_STATUSES, type Project, type ProjectStatus } from "@buildpy/core/modules/project/types";
import DDayBadge from "@/modules/project/ui/d-day-badge";
import PhaseDelayPill from "@/modules/project/ui/phase-delay-pill";
import SiteCalendar from "@/modules/project/ui/site-calendar";
import SiteChart from "@/modules/project/ui/site-chart";
import ProjectStatusBadge from "@/modules/project/ui/status-badge";
import { dDayOf, projectNameOf } from "@buildpy/core/modules/project/view";
import type { ProjectsView } from "@buildpy/core/views/g3a/projects";
import PageHeader from "@/shell/page-header";

export const metadata: Metadata = { title: "현장" };

// 보기 전환 — 목록 · 달력 · 차트(항목이 하나면 전환 줄을 숨긴다).
const VIEW_ITEMS: ViewSwitchItem[] = [
  { value: null, label: "목록" },
  { value: "calendar", label: "달력" },
  { value: "chart", label: "차트" },
];

const WHO_ITEMS = [
  { value: null, label: "전체" },
  { value: "mine", label: "내 담당" },
];

interface RowAction {
  label: string;
  href: string;
  strong: boolean;
}

// 현장 목록 — 오늘 할 일 순(todo-order)이고, 시공중 현장은 오늘 일지·사진이 있는지 한 줄로 보인다.
const Page = ({ data }: { data: ProjectsView }) => {
  const { status, who, view, due, fromParam, q, today, result, board, orphans, siteWarning, unrecorded, listPhasesFailed, phaseRows } = data;
  const projects = result.rows;
  const signals = new Map(Object.entries(data.signals));
  const contractStatusOf = new Map(data.contractStatuses.map((c) => [c.id, c.status]));
  const customerName = new Map(data.customers.map((c) => [c.id, c.name]));
  const memberName = new Map(data.members.map((m) => [m.id, m.name]));
  const phaseNames = new Map(data.phaseDefs.map((p) => [p.code, p.name]));

  const chartMonth = chartMonthOf(fromParam ?? undefined, today);
  const phasesFailed = board ? board.phasesFailed : !view && listPhasesFailed;
  const chart = view === "chart" && board
    ? buildSiteChart({
        projects: board.projects,
        phases: board.phases,
        names: phaseNames,
        assignees: memberName,
        customers: new Map(board.customers.map((c) => [c.id, c.name])),
        today,
        month: chartMonth,
        holidays: board.holidays,
      })
    : null;
  const calendar = view === "calendar" && board
    ? buildSiteCalendar({
        projects: board.projects,
        phases: board.phases,
        phaseName: (code) => phaseNames.get(code) ?? code,
        ownerName: (id) => memberName.get(id),
        month: data.month ?? undefined,
        day: data.day ?? undefined,
        today,
        holidays: board.holidays,
      })
    : null;
  // 달력의 달·고른 날 — 손으로 넣은 값이 받아들여졌을 때만 URL 에 남긴다.
  const monthParam = calendar && data.month === calendar.month ? calendar.month : null;
  const dayParam = calendar && data.day === calendar.selected.date ? calendar.selected.date : null;
  const chartHref = (month: string | null) => listHref("/app/projects", { q, who, status, view: "chart", from: month });

  const phasesOf = new Map<string, SiteChartPhase[]>();
  for (const phase of phaseRows) phasesOf.set(phase.project_id, [...(phasesOf.get(phase.project_id) ?? []), phase]);


  // 칩은 두 줄 — 상태 칩은 착공일 조건을 풀고, 착공일 칩은 시공대기가 아닌 상태를 푼다(착공일 조건은 시공대기에만 걸린다).
  const statusHref = (s: ProjectStatus | null) => listHref("/app/projects", { q, who, status: s, view, from: fromParam, month: monthParam, day: dayParam });
  const dueHref = (d: ProjectDue | null) =>
    listHref("/app/projects", { q, who, status: d && status !== "SCHEDULED" ? null : status, due: d });
  const statusChips: FilterChipItem[] = [
    { href: statusHref(null), label: "전체", count: result.counts.ALL },
    ...PROJECT_STATUSES.map((s) => ({
      href: statusHref(s),
      label: PROJECT_STATUS_LABEL[s],
      count: result.counts[s],
    })),
  ];
  const dueChips: FilterChipItem[] = [
    { href: dueHref(null), label: "전체" },
    { href: dueHref("today"), label: "오늘 착공", count: result.counts.DUE_TODAY },
    { href: dueHref("overdue"), label: "착공일 지남", count: result.counts.DUE_OVERDUE },
  ];

  // 줄마다 주 버튼 하나 — 상세의 주 버튼(projectPrimaryActionOf)과 같은 순서, 착공·사례는 상세에서 누른다.
  const actionOf = (project: Project, signal: ProjectTodaySignals | undefined): RowAction => {
    const detail = `/app/projects/${project.id}`;
    const kind = projectPrimaryActionOf({
      canStart: listStartDue(project, contractStatusOf.get(project.contract_id), today),
      canRecord: RECORDABLE_PROJECT_STATUSES.includes(project.status),
      closed: project.status === "CLOSED",
    });
    if (kind === "record") {
      const behind = needsRecordToday(signal);
      const label = behind && signal?.photoToday === true && !signal.logged ? "일지 쓰기" : "오늘 기록";
      return { label, href: `/app/projects/${project.id}/today`, strong: behind };
    }
    if (kind === "start") return { label: "현장 착공", href: detail, strong: true };
    if (kind === "portfolio") return { label: "사례 만들기", href: detail, strong: false };
    return { label: "현장 보기", href: detail, strong: false };
  };

  const actionButton = (action: RowAction, testId: string) => (
    <Button asChild className="relative z-10 shrink-0" size="sm" variant={action.strong ? "primary" : "outline"}>
      <Link data-testid={testId} href={action.href}>
        {action.label}
      </Link>
    </Button>
  );

  // 오늘 기록 칸 — 쉬는 날은 묻지 않고, 시공중이 아니면 비운다.
  const recordText = (signal: ProjectTodaySignals | undefined) => {
    if (!signal) return <span className="text-muted-foreground">{EMPTY_MARK}</span>;
    if (!signal.workday) return <span className="text-muted-foreground">쉬는 날</span>;
    return (
      <span className={needsRecordToday(signal) ? "text-destructive" : "text-muted-foreground"}>
        사진 {signal.photoToday === null ? "확인 불가" : signal.photoToday ? "있음" : "없음"} · 일지 {signal.logged ? "있음" : "없음"}
      </span>
    );
  };

  // 할 일 알약 — 기록 빠짐 · 공정 지연 · 착공 D-day.
  const todoPills = (project: Project, signal: ProjectTodaySignals | undefined, delayTestId: string) => {
    const gap = recordGapOf(signal);
    return (
      <>
        {gap && (
          <Pill className="whitespace-nowrap" tone="danger">
            {gap}
          </Pill>
        )}
        <PhaseDelayPill
          count={countsPhaseDelay(project.status) ? (signal?.delayed ?? 0) : 0}
          testId={delayTestId}
        />
        {project.status === "SCHEDULED" && (
          <DDayBadge className="shrink-0" days={dDayOf(project.planned_start_date, today)} warnPast />
        )}
      </>
    );
  };

  // 오늘 공정·일정 칸 — 시공중은 지금 공정·며칠째와 공정 진척, 착공 전은 착공일, 준공 뒤는 준공일·유지보수 끝. 표 칸은 두 줄, 카드는 한 줄.
  const scheduleText = (project: Project, testId?: string) => {
    const progress = listPhaseProgressOf(phasesOf.get(project.id) ?? []);
    if (project.status === "IN_PROGRESS") {
      const current = currentPhaseTextOf(phasesOf.get(project.id) ?? [], today, phaseNames);
      const progressText = progress ? (
        <span className={cn("tabular-nums", testId && current && "block text-xs text-muted-foreground")}>
          공정 {progress.done}/{progress.total}
          {progress.end && ` · ${progress.end.slice(5)} 까지`}
        </span>
      ) : (
        <span className={cn("text-muted-foreground", testId && current && "block text-xs")}>
          {listPhasesFailed ? EMPTY_MARK : "공정 없음"}
        </span>
      );
      if (!current) return progressText;
      return (
        <>
          <span className={cn("font-medium text-foreground", testId && "block")} data-testid={testId}>
            {current}
          </span>
          {!testId && " · "}
          {progressText}
        </>
      );
    }
    if (project.status === "SCHEDULED") {
      return <span className="tabular-nums">착공 {project.planned_start_date ?? EMPTY_MARK}</span>;
    }
    return project.completion_date ? (
      <span className="tabular-nums">
        준공 {project.completion_date}
        {project.warranty_end_date && ` · 유지보수 ~${project.warranty_end_date}`}
      </span>
    ) : (
      <span className="text-muted-foreground">{EMPTY_MARK}</span>
    );
  };

  const nameOf = (project: Project) => projectNameOf(project);
  const assigneeOf = (project: Project) => (project.assignee_id && memberName.get(project.assignee_id)) || "미지정";
  const emptyText = q ? "검색어에 맞는 현장이 없습니다." : "이 조건의 현장이 없습니다. 계약이 체결되면 현장이 만들어집니다.";

  // 폰 카드 진척 — 시공중은 공정 막대·「공정 n/m」, 시공대기는 「착공 MM-DD · n일 남음」, 나머지는 일정 한 줄.
  const cardProgress = (project: Project) => {
    const phases = phasesOf.get(project.id) ?? [];
    const progress = project.status === "IN_PROGRESS" ? listPhaseProgressOf(phases) : null;
    if (progress) {
      const current = currentPhaseTextOf(phases, today, phaseNames);
      const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
      return (
        <div className="flex flex-col gap-y-1.5">
          <div aria-hidden className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div className="h-full w-(--w) rounded-full bg-primary" style={{ "--w": `${pct}%` } as CSSProperties} />
          </div>
          <p className="flex min-w-0 justify-between gap-x-2 text-xs text-muted-foreground">
            <span className="shrink-0 tabular-nums">
              공정 {progress.done}/{progress.total}
            </span>
            <span className="min-w-0 truncate">{current ?? (progress.end && `${progress.end.slice(5)} 까지`)}</span>
          </p>
        </div>
      );
    }
    if (project.status === "SCHEDULED") {
      const days = dDayOf(project.planned_start_date, today);
      return (
        <p className="text-xs text-muted-foreground tabular-nums">
          착공 {project.planned_start_date?.slice(5) ?? EMPTY_MARK}
          {days !== null && days > 0 && ` · ${days}일 남음`}
        </p>
      );
    }
    return <p className="truncate text-xs text-muted-foreground">{scheduleText(project)}</p>;
  };

  // 폰 카드 — 현장명·상태 · 고객·담당 · 공정 진척 · 할 일 알약·주 버튼(시안 현장 목록).
  const cards = projects.map((project) => {
    const signal = signals.get(project.id);
    const customer = customerName.get(project.customer_id);
    return (
      <ListCard key={project.id} testId={`project-card-${project.id}`}>
        <div className="flex min-w-0 items-center gap-x-2">
          <Link
            className="min-w-0 flex-1 truncate whitespace-nowrap font-medium after:absolute after:inset-0"
            href={`/app/projects/${project.id}`}
            title={nameOf(project)}>
            {nameOf(project)}
          </Link>
          <ProjectStatusBadge className="shrink-0" status={project.status} />
        </div>
        <p className="truncate text-sm text-muted-foreground">
          {customer ? `${customer} 고객` : EMPTY_MARK} · {assigneeOf(project)}
        </p>
        {cardProgress(project)}
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {todoPills(project, signal, `project-card-delay-${project.id}`)}
          <span className="ml-auto">{actionButton(actionOf(project, signal), `project-card-today-${project.id}`)}</span>
        </div>
        {signal && <p className="truncate text-xs">{recordText(signal)}</p>}
      </ListCard>
    );
  });

  return (
    <>
      <PageHeader
        actions={
          VIEW_ITEMS.length > 1 ? (
            <ViewSwitch aria-label="현장 보기 방식" current={view} items={VIEW_ITEMS} keep={{ q, status, who }} path="/app/projects" testId="project-view-switch" />
          ) : undefined
        }
        meta={
          calendar
            ? siteCalendarMetaOf(calendar.sites)
            : chart
            ? `현장 ${chart.rows.length}곳 · 지연 ${phasesFailed ? EMPTY_MARK : chart.delayedSites} · ${Number(today.slice(5, 7))}월 착공 ${chart.startsThisMonth} · ${Number(today.slice(5, 7))}월 준공 ${chart.endsThisMonth}`
            : unrecorded > 0
              ? `${result.total}건 · 오늘 기록 안 한 현장 ${unrecorded}곳`
              : `${result.total}건`
        }
        title="현장"
      />

      {siteWarning && (
        <Banner tone="warning">
          {siteWarning}
        </Banner>
      )}

      {phasesFailed && <Banner tone="warning">공정 일정을 불러오지 못했습니다. 공정 칸과 지연 수는 잠시 뒤 다시 열어 확인해 주세요.</Banner>}

      {orphans.length > 0 && (
        <SectionCard
          className="flex flex-col gap-y-1 text-sm"
          padding="sm"
          data-testid="orphan-contracts">
          <span className="font-medium">체결됐지만 현장 없는 계약 {orphans.length}건</span>
          <span className="flex flex-wrap gap-x-3 gap-y-1">
            {orphans.map((c) => (
              <Link key={c.id} className="underline underline-offset-2" href={`/app/contracts/${c.id}`}>
                {c.contract_no}
              </Link>
            ))}
          </span>
        </SectionCard>
      )}

      <div className="flex flex-col gap-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <ViewSwitch
            aria-label="담당 범위"
            current={who}
            items={WHO_ITEMS}
            keep={{ q, status, due, view, from: fromParam, month: monthParam, day: dayParam }}
            param="who"
            path="/app/projects"
            testId="project-who-switch"
          />
          {/* 검색칸(min-w-56)+「검색」 이 한 줄에 못 들면 칸째 다음 줄로 — 칸 안에서 단추만 꺾이지 않게. */}
          <div className="min-w-72 flex-1">
            <ListSearch
              action="/app/projects"
              aria-label="현장 검색"
              keep={{ status, due, who, view, from: fromParam, month: monthParam, day: dayParam }}
              nameMatchCapped={result.nameMatchCapped}
              placeholder="현장명 · 현장 주소 · 고객 이름"
              q={q}
            />
          </div>
        </div>
        <FilterChips activeHref={statusHref(status)} aria-label="현장 상태" items={statusChips} />
        {!view && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5" data-testid="project-due-chips">
            <span className="text-xs text-muted-foreground">착공일</span>
            <FilterChips activeHref={dueHref(due)} aria-label="착공일" items={dueChips} />
          </div>
        )}
      </div>

      {calendar ? (
        <SiteCalendar calendar={calendar} keep={{ q, who, status, view: "calendar" }} path="/app/projects" />
      ) : chart ? (
        <SiteChart
          chart={chart}
          nextHref={chartHref(shiftMonth(chartMonth, 1))}
          phasesFailed={phasesFailed}
          prevHref={chartHref(shiftMonth(chartMonth, -1))}
          todayHref={chartHref(null)}
        />
      ) : view ? (
        <EmptyState>
          <span data-testid="project-board-failed">현장 일정을 불러오지 못했습니다. 잠시 뒤 다시 열거나 목록 보기로 확인해 주세요.</span>
        </EmptyState>
      ) : (
        <>
          <ResponsiveList
            cards={cards}
            empty={emptyText}
            label="현장 목록"
            testId="project-cards"
            table={
              <ListTable
                columns={[
                  { label: "현장명", className: "min-w-32" },
                  { label: "담당" },
                  { label: "상태" },
                  { label: "오늘 공정 · 일정" },
                  { label: "오늘 기록" },
                  { label: "할 일" },
                  { label: <span className="sr-only">바로가기</span>, wrap: true },
                ]}
                empty={emptyText}>
                {projects.map((project) => {
                  const signal = signals.get(project.id);
                  return (
                    <TableRow key={project.id} className="relative">
                      <TableCell
                        className="min-w-32 max-w-64 truncate px-3 py-2"
                        title={project.title || project.site_address || undefined}>
                        <Link className="block truncate font-medium after:absolute after:inset-0" href={`/app/projects/${project.id}`}>
                          {nameOf(project)}
                        </Link>
                        <span className="block truncate text-xs text-muted-foreground">
                          {customerName.get(project.customer_id) ?? EMPTY_MARK}
                        </span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-2">{assigneeOf(project)}</TableCell>
                      <TableCell className="px-3 py-2">
                        <ProjectStatusBadge status={project.status} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-2 text-sm">
                        {scheduleText(project, `project-phase-now-${project.id}`)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-2 text-sm">{recordText(signal)}</TableCell>
                      <TableCell className="px-3 py-2">
                        <span className="flex flex-wrap items-center gap-1.5">
                          {todoPills(project, signal, `project-delay-${project.id}`)}
                        </span>
                      </TableCell>
                      <TableCell className="px-3 py-2 text-right">
                        {actionButton(actionOf(project, signal), `project-row-action-${project.id}`)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </ListTable>
            }
          />

          <Pager
            hrefFor={(page) => listHref("/app/projects", { q, who, status, due, page })}
            page={result.page}
            pageCount={result.pageCount}
          />

          <p className="text-xs text-muted-foreground">
            순서: 오늘 기록 안 함 → 공정 지연 → 착공 7일 이내 → 나머지 · 끝난 현장은 맨 뒤
          </p>
        </>
      )}
    </>
  );
};

export default Page;
